// Pure builders for the bootstrap-failure beacon (O2, plans/boot-health.md
// Step 23, #4027). Kept import-free so they load under plain node for unit
// tests — the DOM/fetch orchestration lives in bootstrap-failure.ts.
//
// Why a RAW envelope instead of Sentry.captureException: this code runs from
// main.ts's bootstrapModule catch, where the app is in an unknown broken
// state. The Sentry SDK has usually initialized by then (main.ts's first
// import starts it, before the rest of the app's modules load — see
// app-sentry-init.ts), but the whole point of this beacon is to survive
// states where the normal pipeline does not — so it depends on nothing but
// fetch and hand-built bytes (the PRD calls this out explicitly).

// cap the error text stored/sent — a stack is enough, a megabyte is not
export const BOOTSTRAP_ERROR_TEXT_MAX = 4000;

// derives a single printable text from whatever bootstrapModule rejected with
// (Error, string, or any object) — stack preferred, message next, then String()
export function deriveBootstrapErrorText(err: unknown): string {
  const anyErr = err as any;
  const text: string = (typeof anyErr?.stack === 'string' && anyErr.stack)
    || (typeof anyErr?.message === 'string' && anyErr.message)
    || String(err);
  return text.slice(0, BOOTSTRAP_ERROR_TEXT_MAX);
}

// builds a minimal Sentry envelope (header line + item header + event JSON,
// newline-separated) that the /api/e tunnel forwards verbatim. The event is a
// fatal message event named so it groups as one Sentry issue, with the error
// text in `extra` (message-based grouping would otherwise split per stack).
// `markerError` and `uiRenderError` say which of the handler's other two duties
// failed (#5191) — this beacon is the only place left to say it. Each is left
// out of `extra` when that duty worked, so an ordinary event is unchanged.
export function buildBootstrapFailureEnvelope(input: {
  dsn: string;
  release: string;
  environment: string;
  errorText: string;
  eventId: string; // 32 lowercase hex chars, no dashes
  sentAtIso: string;
  unixSeconds: number;
  markerError?: string;
  uiRenderError?: string;
}): string {
  const extra: Record<string, string> = { error_text: input.errorText };
  if (input.markerError !== undefined) extra.marker_error = input.markerError;
  if (input.uiRenderError !== undefined) extra.ui_render_error = input.uiRenderError;
  const header = JSON.stringify({ dsn: input.dsn, sent_at: input.sentAtIso });
  const itemHeader = JSON.stringify({ type: 'event' });
  const event = JSON.stringify({
    event_id: input.eventId,
    timestamp: input.unixSeconds,
    platform: 'javascript',
    level: 'fatal',
    release: input.release,
    environment: input.environment,
    message: 'WebBootstrapFailed: Angular bootstrapModule rejected',
    // one stable fingerprint — every bootstrap failure lands in ONE issue,
    // with the per-device detail in extra
    fingerprint: ['web-bootstrap-failed'],
    extra,
    tags: { boot_stage: 'bootstrap' },
  });
  return `${header}\n${itemHeader}\n${event}`;
}
